/**
 * Offline-first Infrastructure Service
 *
 * Handles caching, creating, and syncing infrastructure data (critical infrastructure,
 * road closures, utility outages) when offline.
 * Uses IndexedDB for local storage and syncs to the server when online.
 */

import {
  db,
  CachedCriticalInfrastructure,
  CachedRoadClosure,
  CachedUtilityOutage,
} from './db';
import { unwrapServerId } from './unwrap';
import { mustDeferSend, sendTenantOf } from './client-id';
import { normalizeCreateAddressKeys } from './address-keys';
import { api } from '@/services/api';
import { networkService } from '@/services/network.service';
import { useTenantStore } from '@/store/tenant.store';

function generateOfflineId(): string {
  return `offline_${crypto.randomUUID()}`;
}

/**
 * Read the currently-active tenant id (or null if none / SSR).
 */
function getActiveTenantId(): string | null {
  try {
    return useTenantStore.getState().activeTenant?.id ?? null;
  } catch {
    return null;
  }
}

export type InfrastructureType = 'critical' | 'road-closures' | 'utility-outages';

export interface SyncResult {
  success: boolean;
  synced: number;
  failed: number;
  errors: string[];
}

type CachedInfraItem = CachedCriticalInfrastructure | CachedRoadClosure | CachedUtilityOutage;

class InfrastructureOfflineService {
  private getTable(type: InfrastructureType) {
    switch (type) {
      case 'critical':
        return db.cachedCriticalInfrastructure;
      case 'road-closures':
        return db.cachedRoadClosures;
      case 'utility-outages':
        return db.cachedUtilityOutages;
    }
  }

  private getApiPath(type: InfrastructureType): string {
    switch (type) {
      case 'critical':
        return '/infrastructure/critical';
      case 'road-closures':
        return '/infrastructure/road-closures';
      case 'utility-outages':
        return '/infrastructure/utility-outages';
    }
  }

  /**
   * Cache items from server response into IndexedDB
   */
  async cacheFromServer(
    items: Record<string, unknown>[],
    type: InfrastructureType,
    tenantId: string
  ): Promise<{ cached: number; conflicts: number }> {
    const result = { cached: 0, conflicts: 0 };
    const table = this.getTable(type);

    for (const serverItem of items) {
      const id = serverItem.id as string;
      const existing = await table.get(id);

      if (!existing) {
        const cached = this.toCachedItem(serverItem, type, tenantId, true);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Dexie Table union: put() expects a specific cached type but getTable() returns a union
        await table.put(cached as any);
        result.cached++;
      } else if (existing.pending_changes) {
        result.conflicts++;
      } else if (
        new Date(serverItem.updated_at as string) > new Date(existing.updated_at)
      ) {
        const cached = this.toCachedItem(serverItem, type, tenantId, true);
        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Dexie Table union: put() expects a specific cached type but getTable() returns a union
        await table.put(cached as any);
        result.cached++;
      }
    }

    return result;
  }

  /**
   * Get cached items from IndexedDB
   */
  async getCached(
    tenantId: string,
    type: InfrastructureType,
    filters?: { incident_id?: string; status?: string; category?: string; utility_type?: string }
  ): Promise<CachedInfraItem[]> {
    const table = this.getTable(type);
    const raw = await table.where('tenant_id').equals(tenantId).toArray();
    let results: CachedInfraItem[] = raw as CachedInfraItem[];

    if (filters?.incident_id) {
      results = results.filter((r) => r.incident_id === filters.incident_id);
    }
    if (filters?.status) {
      results = results.filter((r) => r.status === filters.status);
    }
    if (filters?.category && type === 'critical') {
      results = results.filter(
        (r) => (r as CachedCriticalInfrastructure).category === filters.category
      );
    }
    if (filters?.utility_type && type === 'utility-outages') {
      results = results.filter(
        (r) => (r as CachedUtilityOutage).utility_type === filters.utility_type
      );
    }

    return results;
  }

  /**
   * Create a new item offline with an offline ID
   */
  async createOffline(
    data: Record<string, unknown>,
    type: InfrastructureType,
    tenantId: string
  ): Promise<CachedInfraItem> {
    const offlineId = generateOfflineId();
    const now = new Date().toISOString();

    const item = this.toCachedItem(
      { ...data, id: offlineId, created_at: now, updated_at: now },
      type,
      tenantId,
      false
    );
    item.offline_id = offlineId;
    item.pending_changes = true;

    const table = this.getTable(type);
    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Dexie Table union: put() expects a specific cached type but getTable() returns a union
    await table.put(item as any);

    if (networkService.isOnline()) {
      await this.syncItem(offlineId, type);
    }

    return item;
  }

  /**
   * Update an existing cached item, marking it as pending sync
   */
  async updateOffline(
    id: string,
    data: Record<string, unknown>,
    type: InfrastructureType
  ): Promise<CachedInfraItem | null> {
    const table = this.getTable(type);
    const existing = await table.get(id);
    if (!existing) return null;

    const updated = {
      ...existing,
      ...data,
      data: { ...(existing.data || {}), ...data },
      updated_at: new Date().toISOString(),
      is_synced: false,
      pending_changes: true,
    };

    // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Dexie Table union: put() expects a specific cached type but getTable() returns a union
    await table.put(updated as any);

    if (networkService.isOnline()) {
      await this.syncItem(id, type);
    }

    return updated as CachedInfraItem;
  }

  /**
   * Sync all pending items of a given type to the server
   */
  async syncAll(type: InfrastructureType): Promise<SyncResult> {
    const result: SyncResult = {
      success: true,
      synced: 0,
      failed: 0,
      errors: [],
    };

    if (!networkService.isOnline()) {
      result.success = false;
      result.errors.push('No network connection');
      return result;
    }

    const table = this.getTable(type);
    const pending = (await table.filter((item) => !item.is_synced).toArray()) as CachedInfraItem[];
    const activeTenantId = getActiveTenantId();

    for (const item of pending) {
      // Never sent as someone else (S-06): another originator's row waits for them, or for the handoff.
      if (mustDeferSend(item as never)) continue;
      // Tenant guard: never replay an item against a different tenant than the
      // one it was created under (the api client injects the ambient X-Tenant-ID).
      // Defer (leave pending) — it will sync when its own tenant is active again.
      if (item.tenant_id != null && activeTenantId != null && item.tenant_id !== activeTenantId) {
        continue;
      }
      const success = await this.syncItem(item.id, type);
      if (success) {
        result.synced++;
      } else {
        result.failed++;
        result.errors.push(`Failed to sync ${type} item: ${item.name || item.id}`);
      }
    }

    result.success = result.failed === 0;
    return result;
  }

  /**
   * Clear cached data, optionally filtered by tenant and/or type
   */
  async clearCache(tenantId?: string, type?: InfrastructureType): Promise<void> {
    const types: InfrastructureType[] = type
      ? [type]
      : ['critical', 'road-closures', 'utility-outages'];

    for (const t of types) {
      const table = this.getTable(t);
      if (tenantId) {
        await table.where('tenant_id').equals(tenantId).delete();
      } else {
        await table.clear();
      }
    }
  }

  /**
   * Get count of pending (unsynced) items
   */
  async getPendingCount(type?: InfrastructureType): Promise<number> {
    const types: InfrastructureType[] = type
      ? [type]
      : ['critical', 'road-closures', 'utility-outages'];

    let count = 0;
    for (const t of types) {
      const table = this.getTable(t);
      count += await table.filter((item) => !item.is_synced).count();
    }
    return count;
  }

  /**
   * Sync a single item to the server
   */
  private async syncItem(id: string, type: InfrastructureType): Promise<boolean> {
    const table = this.getTable(type);
    const item = await table.get(id);
    if (!item || item.is_synced) return true;
    // Contract 9 gate (client-id.ts mustDeferSend): never sent as someone else.
    if (mustDeferSend(item)) return false;

    const apiPath = this.getApiPath(type);

    try {
      if (item.offline_id && item.id === item.offline_id) {
        // New offline-created item -- POST to server.
        // These endpoints return the BARE entity (infrastructure.controller.ts
        // does res.status(201).json(closure) / json(outage) / json(infrastructure)),
        // not { data: ... }. Reading response.data.id threw AFTER the server had
        // created the row, leaving the record unsynced with its offline id — so
        // every reconnect POSTed it again and one field-reported road closure
        // became N duplicates on the EOC map.
        // Road closures and hazards (map-first capture, migration 680): a blank address
        // is sent as NO address so the server fills it from the point, and a typed one
        // keeps address_source. Other infrastructure types keep their payload as is.
        const body = type === 'road-closures' ? normalizeCreateAddressKeys(item.data ?? {}) : item.data;
        const response = await api.post<unknown>(apiPath, body, sendTenantOf(item));
        const serverId = unwrapServerId(response);
        if (!serverId) {
          throw new Error(`${type} create returned no server id — refusing to mark synced`);
        }

        // Replace offline record with server ID. Atomic so a failure here cannot
        // leave the row deleted-but-not-reinserted.
        await db.transaction('rw', table, async () => {
          await table.delete(item.id);
          // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Dexie Table union: put() expects a specific cached type but getTable() returns a union
          await table.put({
            ...item,
            id: serverId,
            is_synced: true,
            synced_at: new Date().toISOString(),
            pending_changes: false,
            last_error: undefined,
          } as any);
        });
      } else {
        // Existing item -- PATCH to server
        await api.patch(`${apiPath}/${item.id}`, item.data, sendTenantOf(item));

        // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Dexie Table union: update() expects specific type
        await table.update(item.id, {
          is_synced: true,
          synced_at: new Date().toISOString(),
          pending_changes: false,
          last_error: undefined,
        } as any);
      }

      return true;
    } catch (error) {
      // eslint-disable-next-line @typescript-eslint/no-explicit-any -- Dexie Table union: update() expects specific type
      await table.update(item.id, {
        last_error: (error as Error).message,
      } as any);
      return false;
    }
  }

  /**
   * Convert a server response item into a cached IndexedDB record
   */
  private toCachedItem(
    serverItem: Record<string, unknown>,
    type: InfrastructureType,
    tenantId: string,
    isSynced: boolean
  ): CachedInfraItem {
    const base = {
      id: serverItem.id as string,
      tenant_id: tenantId,
      incident_id: serverItem.incident_id as string | undefined,
      name: (serverItem.name as string) || '',
      status: (serverItem.status as string) || '',
      data: serverItem,
      is_synced: isSynced,
      synced_at: isSynced ? new Date().toISOString() : undefined,
      pending_changes: false,
      updated_at: (serverItem.updated_at as string) || new Date().toISOString(),
    };

    if (type === 'critical') {
      return {
        ...base,
        category: (serverItem.category as string) || '',
      } as CachedCriticalInfrastructure;
    }

    if (type === 'utility-outages') {
      return {
        ...base,
        utility_type: (serverItem.utility_type as string) || '',
      } as CachedUtilityOutage;
    }

    return base as CachedRoadClosure;
  }
}

export const infrastructureOfflineService = new InfrastructureOfflineService();
